import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { ConversationAssigneeGroup, useConversationAssigneeGroups } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

interface GroupOption extends Option {
  data: ConversationAssigneeGroup;
}

enum UpdateUserAssigneeGroupsFormField {
  GroupOptions = 'groupOptions',
}

interface FormValues {
  [UpdateUserAssigneeGroupsFormField.GroupOptions]: GroupOption[];
}

export const UpdateUserAssigneeGroupsModal = () => {
  const { closeModal } = useModalMethods(Modals.UpdateAssigneeGroups);
  const { isOpened, modalProps } = useModalState(Modals.UpdateAssigneeGroups);

  const { conversationAssigneeGroups } = useConversationAssigneeGroups(
    modalProps?.user.partnerId,
  );

  const groupOptions = useMemo(() => {
    return Object.values(conversationAssigneeGroups ?? {}).map<GroupOption>((group) => ({
      id: group.id,
      label: group.name,
      partnerId: group.partnerId,
      value: group.conversationAssigneeGroupId,
      data: group,
    }));
  }, [conversationAssigneeGroups]);

  const selectedOptions = useMemo(() => {
    const selectedGroupIds = (modalProps?.user.conversationAssigneeGroups ?? []).map(
      (g) => g.id,
    );
    return groupOptions.filter((g) => selectedGroupIds.includes(g.id));
  }, [modalProps, groupOptions]);

  const methods = useForm<FormValues>({
    mode: 'all',
    values: {
      [UpdateUserAssigneeGroupsFormField.GroupOptions]: selectedOptions,
    },
  });

  const { formState, reset, handleSubmit } = methods;

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onFormSubmit = useCallback(
    (formValues: FormValues) => {
      if (!modalProps?.user.id) {
        return;
      }
      modalProps?.onSubmit({
        userId: modalProps.user.id,
        userConversationAssigneeGroups: formValues[
          UpdateUserAssigneeGroupsFormField.GroupOptions
        ].map((group) => group.data),
      });
      closeModalAndResetForm();
    },
    [modalProps, closeModalAndResetForm],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Update Assignee Groups"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'update-user-assignee-groups-cancel' },
        {
          label: 'Save groups',
          kind: 'primary',
          onClick: handleSubmit(onFormSubmit),
          disabled: !formState.isValid,
          testId: 'update-user-assignee-groups-submit',
        },
      ]}
      onClose={closeModalAndResetForm}
      data-testid="update-user-assignee-groups-modal"
    >
      <GenericForm<FormValues> formMethods={methods}>
        <FormAutocomplete
          type="autocomplete"
          data-testid="update-user-assignee-groups-groups"
          multiple
          name={UpdateUserAssigneeGroupsFormField.GroupOptions}
          label="User Assignee Groups"
          options={groupOptions}
        />
      </GenericForm>
    </ModalContainer>
  );
};
